// Where web push can reach someone at all, and when it is fair to ask.
//
// Everything here is synchronous and side-effect free apart from reading
// localStorage, so the home card can decide without a network round-trip and
// without ever triggering the browser's own permission dialog.

import { isMiniapp } from 'modules/miniapp'

export type PushAvailability =
    // A normal browser tab or an installed PWA that can subscribe.
    | 'ok'
    // A Bale / Telegram / Eitaa webview. They expose no push API that reaches
    // the phone, and asking there would only produce a dialog that does nothing.
    | 'miniapp'
    // Safari on iPhone/iPad in a tab. Apple only delivers web push to a site
    // that was added to the home screen and opened from there.
    | 'ios_browser'
    // No service worker, PushManager or Notification (old browsers, in-app
    // browsers of other apps).
    | 'unsupported'

export const isIOS = (
    userAgent: string = navigator.userAgent,
    maxTouchPoints: number = navigator.maxTouchPoints || 0
): boolean =>
    /iphone|ipad|ipod/i.test(userAgent) ||
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    (/macintosh/i.test(userAgent) && maxTouchPoints > 1)

export type DevicePlatform = 'android' | 'ios' | 'macos' | 'windows' | 'other'

/** Where the OS's own notification switches are, which is what a test failure points at. */
export const devicePlatform = (
    userAgent: string = navigator.userAgent,
    maxTouchPoints: number = navigator.maxTouchPoints || 0
): DevicePlatform => {
    if (/android/i.test(userAgent)) return 'android'
    if (isIOS(userAgent, maxTouchPoints)) return 'ios'
    if (/windows/i.test(userAgent)) return 'windows'
    if (/macintosh|mac os x/i.test(userAgent)) return 'macos'
    return 'other'
}

/** The browser's name as the OS lists it in notification settings. */
export const browserName = (userAgent: string = navigator.userAgent): string => {
    if (/edg(a|ios)?\//i.test(userAgent)) return 'Microsoft Edge'
    if (/samsungbrowser\//i.test(userAgent)) return 'Samsung Internet'
    if (/opr\//i.test(userAgent)) return 'Opera'
    if (/firefox\/|fxios\//i.test(userAgent)) return 'Firefox'
    if (/chrome\/|crios\//i.test(userAgent)) return 'Google Chrome'
    if (/safari\//i.test(userAgent)) return 'Safari'
    return 'مرورگر'
}

export const isStandalone = (): boolean => {
    try {
        if (window.matchMedia?.('(display-mode: standalone)').matches)
            return true
    } catch {
        /* very old webviews */
    }
    return (navigator as Navigator & { standalone?: boolean }).standalone === true
}

export const pushAvailability = (): PushAvailability => {
    if (isMiniapp()) return 'miniapp'
    // Checked before the API probe: iOS Safari in a tab has no PushManager, and
    // "add us to the home screen" is a much better answer than "unsupported".
    if (isIOS() && !isStandalone()) return 'ios_browser'
    if (
        !('serviceWorker' in navigator) ||
        !('PushManager' in window) ||
        !('Notification' in window)
    )
        return 'unsupported'
    return 'ok'
}

export const notificationPermission = (): NotificationPermission | null =>
    'Notification' in window ? Notification.permission : null

// ── the soft ask ────────────────────────────────────────────────────────────
//
// The browser gives a site exactly one real permission dialog: a «Block» there
// is permanent unless the person digs through site settings. So the browser
// dialog is only ever opened from our own card, after the person has read what
// they would get and tapped «فعال‌سازی». «الان نه» on our card costs nothing and
// is respected: two weeks of silence, and after the third time we stop asking
// for good. The settings page stays available either way.

const PROMPT_KEY = 'webpush:prompt'
export const PROMPT_SNOOZE_DAYS = 14
export const PROMPT_MAX_DISMISSALS = 3
const DAY_MS = 24 * 60 * 60 * 1000

export interface PromptState {
    dismissals: number
    snoozedUntil: number
}

const EMPTY_PROMPT: PromptState = { dismissals: 0, snoozedUntil: 0 }

export const readPromptState = (): PromptState => {
    try {
        const raw = window.localStorage.getItem(PROMPT_KEY)
        if (!raw) return EMPTY_PROMPT
        const parsed = JSON.parse(raw)
        return {
            dismissals: Number(parsed?.dismissals) || 0,
            snoozedUntil: Number(parsed?.snoozedUntil) || 0,
        }
    } catch {
        return EMPTY_PROMPT
    }
}

export const isPromptAllowed = (state: PromptState, now: number): boolean =>
    state.dismissals < PROMPT_MAX_DISMISSALS && now >= state.snoozedUntil

export const nextPromptState = (
    state: PromptState,
    now: number
): PromptState => ({
    dismissals: state.dismissals + 1,
    snoozedUntil: now + PROMPT_SNOOZE_DAYS * DAY_MS,
})

export const rememberPromptDismissed = (now: number = Date.now()) => {
    const next = nextPromptState(readPromptState(), now)
    try {
        window.localStorage.setItem(PROMPT_KEY, JSON.stringify(next))
    } catch {
        /* best effort: worst case the card comes back next visit */
    }
    return next
}

// ── what this browser is subscribed as ─────────────────────────────────────
//
// Written only after the backend accepted the subscription. It lives in
// localStorage on purpose: logout and an expired session both clear
// localStorage, so a browser subscription found WITHOUT this record belongs to
// a session that is gone — see `syncWebPush`.

const STORED_KEY = 'webpush:subscription'

export interface StoredSubscription {
    endpoint: string
    // The VAPID public key it was created with; announce rotating its key
    // silently breaks every subscription made with the old one.
    publicKey: string
    syncedAt: number
}

export const readStoredSubscription = (): StoredSubscription | null => {
    try {
        const raw = window.localStorage.getItem(STORED_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        return parsed?.endpoint ? (parsed as StoredSubscription) : null
    } catch {
        return null
    }
}

export const writeStoredSubscription = (value: StoredSubscription | null) => {
    try {
        if (value) window.localStorage.setItem(STORED_KEY, JSON.stringify(value))
        else window.localStorage.removeItem(STORED_KEY)
    } catch {
        /* best effort */
    }
}

/** VAPID keys travel as base64url; PushManager wants the raw bytes. */
export const urlBase64ToUint8Array = (base64: string): Uint8Array => {
    const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
        .replace(/-/g, '+')
        .replace(/_/g, '/')
    const raw = window.atob(padded)
    const bytes = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i)
    return bytes
}

export const arrayBufferToUrlBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.length; i += 1)
        binary += String.fromCharCode(bytes[i])
    return window
        .btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
}

// ── opening a notification ─────────────────────────────────────────────────
//
// The service worker opens the notification's page with `pn` (announce's
// message id) and `pc` (category) appended, so a cold start can still say
// which notification brought the person in. The app strips them again.

export interface PushOpen {
    messageId: number | null
    category: string
    cleanPath: string
}

export const readPushOpen = (
    pathname: string,
    search: string
): PushOpen | null => {
    const params = new URLSearchParams(search)
    if (!params.has('pn') && !params.has('pc')) return null
    const id = Number(params.get('pn'))
    const category = params.get('pc') || ''
    params.delete('pn')
    params.delete('pc')
    const rest = params.toString()
    return {
        messageId: Number.isInteger(id) && id > 0 ? id : null,
        category,
        cleanPath: rest ? `${pathname}?${rest}` : pathname,
    }
}
